import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { io } from 'socket.io-client';
import { tripAPI, boardingAPI } from '../services/api';

const STATUS_COLORS = {
  scheduled:   'bg-blue-100 text-blue-700',
  boarding:    'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-orange-100 text-orange-700',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-red-100 text-red-700',
};
const STATUS_LABELS = {
  scheduled: 'مجدولة', boarding: 'الصعود', in_progress: 'في الطريق',
  completed: 'منتهية', cancelled: 'ملغاة',
};

const BoardingPage = () => {
  const todayStr = new Date().toISOString().split('T')[0]; // 'YYYY-MM-DD'

  const [allTrips, setAllTrips]         = useState([]);
  const [filterDate, setFilterDate]     = useState(todayStr);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [passengers, setPassengers]     = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [passLoading, setPassLoading]   = useState(false);

  // Derive visible trips: match filterDate; on today also show in_progress/boarding
  const trips = allTrips.filter((t) => {
    const tripDate = t.departureTime.slice(0, 10);
    const isToday  = filterDate === todayStr;
    if (isToday && ['in_progress', 'boarding'].includes(t.status)) return true;
    return tripDate === filterDate;
  });

  const [bookingInput, setBookingInput] = useState('');
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [lastResult, setLastResult]     = useState(null);

  // GPS tracking
  const [gpsTracking, setGpsTracking] = useState(false);
  const [gpsCoords, setGpsCoords]     = useState(null);
  const socketRef      = useRef(null);
  const gpsIntervalRef = useRef(null);
  const restOnlyRef    = useRef(false); // true when socket auth failed; REST-only mode

  // QR camera scanner
  const [scannerOpen, setScannerOpen]     = useState(false);
  const [scanResult, setScanResult]       = useState(null); // { success, message, passengerName, seat, alreadyBoarded }
  const [scanLoading, setScanLoading]     = useState(false);
  const [cameraError, setCameraError]     = useState('');
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const rafRef      = useRef(null);
  const detectorRef = useRef(null);

  const inputRef = useRef(null);

  useEffect(() => {
    tripAPI.getCompanyTrips()
      .then((res) => {
        const nonCancelled = (res.data.data || []).filter((t) => t.status !== 'cancelled');
        setAllTrips(nonCancelled);
        // Auto-select today's first trip
        const todayTrip = nonCancelled.find((t) => t.departureTime.slice(0, 10) === todayStr);
        if (todayTrip) setSelectedTrip(todayTrip);
      })
      .catch(() => toast.error('فشل في تحميل الرحلات'))
      .finally(() => setTripsLoading(false));
  }, [todayStr]);

  const loadPassengers = (tripId) => {
    setPassLoading(true);
    tripAPI.getPassengers(tripId)
      .then((res) => setPassengers(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل قائمة المسافرين'))
      .finally(() => setPassLoading(false));
  };

  useEffect(() => {
    if (selectedTrip) loadPassengers(selectedTrip.id);
    else setPassengers([]);
  }, [selectedTrip]);

  // GPS: connect socket and emit location on interval
  const stopGps = useCallback(() => {
    restOnlyRef.current = false;
    clearInterval(gpsIntervalRef.current);
    gpsIntervalRef.current = null;
    socketRef.current?.disconnect();
    socketRef.current = null;
    setGpsTracking(false);
    setGpsCoords(null);
  }, []);

  // Reads socket from ref so it works in both socket-mode and REST-only mode
  const emitLocation = useCallback((tripId) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        setGpsCoords({ lat, lng });
        const sock = socketRef.current;
        if (sock?.connected) {
          sock.emit('driver:updateLocation', { tripId, lat, lng });
        }
        tripAPI.updateGps(tripId, { lat, lng }).catch(() => {});
      },
      () => toast.warn('تعذّر الحصول على الموقع'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const startGps = useCallback(() => {
    if (!selectedTrip) return;
    if (!navigator.geolocation) { toast.error('المتصفح لا يدعم GPS'); return; }

    // Bug fix: a plain-string .replace('/api', '') strips the FIRST match of
    // "/api" anywhere in the URL — since the production API host is
    // api.safer-sdn.com, that matched the "api" subdomain instead of the
    // trailing path, producing a garbage host ("https") the socket could
    // never connect to. Anchored regex matches how every other file in this
    // codebase strips the trailing /api segment.
    const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    // Auth now rides on the httpOnly safer_session cookie (see app.js's
    // socket.io middleware) — there is no client-readable token to attach
    // manually, and there never has been since the cookie migration.
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setGpsTracking(true);
      emitLocation(selectedTrip.id);
      gpsIntervalRef.current = setInterval(() => emitLocation(selectedTrip.id), 15_000);
    });

    socket.on('disconnect', () => {
      // Only stop GPS if this was a normal disconnect (not a switch to REST-only mode)
      if (!restOnlyRef.current) {
        clearInterval(gpsIntervalRef.current);
        gpsIntervalRef.current = null;
        setGpsTracking(false);
      }
    });

    socket.on('error', (err) => {
      if (err?.message === 'Unauthorized') {
        // Socket auth rejected — drop the socket but keep the REST interval running
        restOnlyRef.current = true;
        socket.disconnect();
        socketRef.current = null;
      }
    });
  }, [selectedTrip, emitLocation]);

  // Cleanup on unmount or trip change
  useEffect(() => () => stopGps(), [stopGps]);

  // ── QR Scanner ──────────────────────────────────────────────────────────────
  const stopScanner = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScannerOpen(false);
  }, []);

  const processQrValue = useCallback(async (raw) => {
    if (scanLoading) return;
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await boardingAPI.scanBoard(raw);
      const d = res.data;
      setScanResult({
        success: true,
        alreadyBoarded: d.alreadyBoarded,
        message: d.alreadyBoarded
          ? `✅ مسجّل مسبقاً — ${d.data?.passengerName}`
          : `✅ تم التسجيل — ${d.data?.passengerName}`,
        passengerName: d.data?.passengerName,
        seat: d.data?.seat,
      });
      if (!d.alreadyBoarded) {
        // Update in-page passenger list so staff sees the change immediately
        const parsed = (() => { try { return JSON.parse(raw); } catch { return {}; } })();
        setPassengers((prev) =>
          prev.map((b) =>
            b.bookingNumber === parsed.booking ? { ...b, boardedAt: new Date().toISOString() } : b
          )
        );
      }
    } catch (err) {
      setScanResult({ success: false, message: err.response?.data?.message || 'تذكرة غير صالحة' });
    } finally {
      setScanLoading(false);
    }
  }, [scanLoading]);

  const startScanner = useCallback(async () => {
    setCameraError('');
    setScanResult(null);

    if (!('BarcodeDetector' in window)) {
      setCameraError('المتصفح لا يدعم مسح QR — استخدم Chrome على Android أو Edge');
      setScannerOpen(true);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setScannerOpen(true);

      // Give React time to mount the video element
      await new Promise((r) => setTimeout(r, 200));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      if (!detectorRef.current) {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
      }

      const scan = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(scan);
          return;
        }
        try {
          const codes = await detectorRef.current.detect(videoRef.current);
          if (codes.length > 0) {
            const raw = codes[0].rawValue;
            stopScanner();
            await processQrValue(raw);
            return;
          }
        } catch { /* ignore frame errors */ }
        rafRef.current = requestAnimationFrame(scan);
      };

      rafRef.current = requestAnimationFrame(scan);
    } catch (err) {
      setCameraError('تعذّر فتح الكاميرا. تأكد من منح الإذن.');
      setScannerOpen(true);
    }
  }, [stopScanner, processQrValue]);

  // Cleanup scanner on unmount
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    const num = bookingInput.trim().toUpperCase();
    if (!num) return;
    setCheckInLoading(true);
    setLastResult(null);
    try {
      const res = await boardingAPI.checkIn(num);
      setLastResult({ success: true, message: res.data.message, seats: res.data.data?.seats });
      setBookingInput('');
      // Update passenger list in place
      setPassengers((prev) =>
        prev.map((b) =>
          b.bookingNumber === num ? { ...b, boardedAt: new Date().toISOString() } : b
        )
      );
    } catch (err) {
      setLastResult({ success: false, message: err.response?.data?.message || 'خطأ في التسجيل' });
    } finally {
      setCheckInLoading(false);
      inputRef.current?.focus();
    }
  };

  // Returns the name(s) as they appear on the ticket.
  // passengerDetails holds per-seat names entered at booking time; fall back to account name.
  const ticketName = (b) => {
    const details = b.passengerDetails;
    if (Array.isArray(details) && details.length > 0) {
      return details.map((d) => d.name).filter(Boolean).join(' / ');
    }
    return b.passenger?.name || '—';
  };

  const boarded    = passengers.filter((b) => b.boardedAt);
  const notBoarded = passengers.filter((b) => !b.boardedAt);

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">🎫 تسجيل الصعود</h1>
        <p className="text-gray-500 text-sm">سجّل حضور المسافرين عند صعودهم للباص</p>
      </div>

      {/* Trip selector */}
      <div className="bg-white rounded-xl shadow p-4">
        {/* Header: label + date filter */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <label className="text-sm font-semibold text-gray-700">اختر الرحلة</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setFilterDate(todayStr); setSelectedTrip(null); }}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${
                filterDate === todayStr
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              اليوم
            </button>
            <input
              type="date"
              value={filterDate}
              min={todayStr}
              onChange={(e) => { setFilterDate(e.target.value); setSelectedTrip(null); }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-gray-700"
            />
          </div>
        </div>

        {tripsLoading ? (
          <p className="text-gray-400 text-sm">جاري التحميل...</p>
        ) : trips.length === 0 ? (
          <p className="text-gray-400 text-sm">
            {filterDate === todayStr ? 'لا توجد رحلات اليوم' : `لا توجد رحلات بتاريخ ${filterDate}`}
          </p>
        ) : (
          <div className="space-y-2">
            {trips.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTrip(t)}
                className={`w-full text-right flex items-center justify-between px-4 py-3 rounded-xl border transition
                  ${selectedTrip?.id === t.id
                    ? 'bg-blue-50 border-blue-300 text-blue-800'
                    : 'bg-gray-50 border-gray-200 hover:border-blue-200 text-gray-700'}`}
              >
                <div>
                  <p className="font-semibold">{t.fromCity} → {t.toCity}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {format(new Date(t.departureTime), 'yyyy/MM/dd HH:mm')} · {t.bus?.model} ({t.bus?.plateNumber})
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status]}`}>
                    {STATUS_LABELS[t.status]}
                  </span>
                  <span className="text-xs text-gray-400">{t.bookedSeats?.length || 0} حجز</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedTrip && (
        <>
          {/* Check-in: QR scan + manual input */}
          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <h2 className="font-bold text-gray-700">تسجيل الصعود</h2>

            {/* QR Camera Scanner */}
            <button
              onClick={scannerOpen ? stopScanner : startScanner}
              disabled={scanLoading}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition
                ${scannerOpen
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-blue-700 hover:bg-blue-800 text-white'}`}
            >
              <span className="text-lg">{scannerOpen ? '✕' : '📷'}</span>
              {scannerOpen ? 'إغلاق الكاميرا' : 'مسح QR بالكاميرا'}
            </button>

            {/* Live camera feed */}
            {scannerOpen && (
              <div className="relative rounded-xl overflow-hidden bg-black">
                {cameraError ? (
                  <div className="flex items-center justify-center h-40 text-red-400 text-sm px-4 text-center">{cameraError}</div>
                ) : (
                  <>
                    <video ref={videoRef} playsInline muted className="w-full rounded-xl max-h-64 object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-48 border-4 border-blue-400 rounded-xl opacity-70" />
                    </div>
                    <p className="absolute bottom-2 inset-x-0 text-center text-white text-xs opacity-80">
                      وجّه الكاميرا نحو رمز QR على التذكرة
                    </p>
                  </>
                )}
              </div>
            )}

            {/* QR scan result */}
            {scanLoading && (
              <div className="flex items-center gap-2 text-blue-600 text-sm px-2">
                <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                جاري التحقق من التذكرة...
              </div>
            )}
            {scanResult && (
              <div className={`px-4 py-3 rounded-xl text-sm font-medium flex items-start gap-2
                ${scanResult.success
                  ? (scanResult.alreadyBoarded ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-green-50 text-green-700 border border-green-200')
                  : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <span className="text-lg mt-0.5">{scanResult.success ? (scanResult.alreadyBoarded ? '⚠️' : '✅') : '❌'}</span>
                <div>
                  <p>{scanResult.message}</p>
                  {scanResult.seat && <p className="text-xs opacity-75 mt-0.5">مقعد رقم {scanResult.seat}</p>}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3 text-gray-300">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">أو أدخل رقم الحجز يدوياً</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Manual input */}
            <form onSubmit={handleCheckIn} className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={bookingInput}
                onChange={(e) => setBookingInput(e.target.value)}
                placeholder="SFR-XXXXXXXX"
                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 uppercase"
                dir="ltr"
              />
              <button
                type="submit"
                disabled={checkInLoading || !bookingInput.trim()}
                className="bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300 text-white px-6 py-3 rounded-xl text-sm font-semibold transition"
              >
                {checkInLoading ? '...' : 'تسجيل ✓'}
              </button>
            </form>

            {/* Manual check-in result */}
            {lastResult && (
              <div className={`px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2
                ${lastResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <span className="text-lg">{lastResult.success ? '✅' : '❌'}</span>
                <span>{lastResult.message}</span>
                {lastResult.seats && (
                  <span className="text-xs opacity-75">— مقاعد: {lastResult.seats.join(', ')}</span>
                )}
              </div>
            )}
          </div>

          {/* GPS Tracking */}
          <div className="bg-white rounded-xl shadow p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-bold text-gray-700">📍 مشاركة الموقع</h2>
                <p className="text-xs text-gray-400 mt-0.5">يتيح لبوابة الشركة مراقبة موقع الباص مباشرة</p>
              </div>
              <button
                onClick={gpsTracking ? stopGps : startGps}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                  gpsTracking
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {gpsTracking ? '⏹ إيقاف التتبع' : '▶ بدء التتبع'}
              </button>
            </div>
            {gpsTracking && gpsCoords && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-green-600 text-xl">🟢</span>
                <div>
                  <p className="text-green-700 font-semibold text-sm">جاري مشاركة الموقع</p>
                  <p className="text-green-600 text-xs font-mono">
                    {gpsCoords.lat.toFixed(5)}, {gpsCoords.lng.toFixed(5)}
                  </p>
                </div>
              </div>
            )}
            {!gpsTracking && (
              <p className="text-gray-400 text-xs">اضغط "بدء التتبع" لمشاركة موقع الباص في الوقت الفعلي مع بوابة الشركة.</p>
            )}
          </div>

          {/* Boarding stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{passengers.length}</p>
              <p className="text-xs text-gray-500 mt-1">إجمالي الحجوزات</p>
            </div>
            <div className="bg-green-50 rounded-xl shadow p-4 text-center border border-green-100">
              <p className="text-2xl font-bold text-green-700">{boarded.length}</p>
              <p className="text-xs text-green-600 mt-1">صعدوا الباص</p>
            </div>
            <div className="bg-red-50 rounded-xl shadow p-4 text-center border border-red-100">
              <p className="text-2xl font-bold text-red-600">{notBoarded.length}</p>
              <p className="text-xs text-red-500 mt-1">لم يصلوا بعد</p>
            </div>
          </div>

          {/* Passenger list */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
              <span className="font-semibold text-gray-700 text-sm">قائمة المسافرين</span>
              <button onClick={() => loadPassengers(selectedTrip.id)}
                className="text-xs text-blue-600 hover:text-blue-800">تحديث</button>
            </div>

            {passLoading ? (
              <div className="py-10 text-center text-gray-400 text-sm">جاري التحميل...</div>
            ) : passengers.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">
                <p className="text-2xl mb-2">🪑</p>لا توجد حجوزات مؤكدة
              </div>
            ) : (
              <div className="divide-y">
                {/* Not boarded first */}
                {notBoarded.map((b) => (
                  <div key={b.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{ticketName(b)}</p>
                      <p className="text-xs text-gray-400 font-mono">{b.bookingNumber}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">
                        💺 {b.seats?.join(', ')}
                      </span>
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-medium">
                        لم يصل
                      </span>
                    </div>
                  </div>
                ))}
                {/* Boarded (dimmed) */}
                {boarded.map((b) => (
                  <div key={b.id} className="px-5 py-3 flex items-center justify-between bg-green-50/50 opacity-70">
                    <div>
                      <p className="font-medium text-gray-600 text-sm">{ticketName(b)}</p>
                      <p className="text-xs text-gray-400 font-mono">{b.bookingNumber}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">
                        💺 {b.seats?.join(', ')}
                      </span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                        ✓ صعد
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BoardingPage;
